"use client";

import {
  getDateInputRejection,
  getDateRangeOrderError,
  isReasonableDate,
} from "@/lib/utils/filter-utils";
import { shiftLocalDays, toLocalIsoDate } from "@/lib/utils/date-formatter";
import { cn } from "@/lib/utils/general-utils";
import { CalendarDays, ChevronsUpDown, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// =============================================================================
// TYPES
// =============================================================================

export interface DateRangeValue {
  from: string;
  to: string;
  presetLabel?: string;
}

interface DateRangePreset {
  label: string;
  from: string;
  to: string;
}

export interface DateRangeDropdownProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** When provided, only the preset with this label is highlighted (avoids
   *  ambiguity when two presets resolve to the same date range). */
  activePresetLabel?: string;
  /** When true, the dropdown stays open after clearing the value */
  stayOpenOnClear?: boolean;
  /** When true and a range is selected, prefix the trigger with `{placeholder}: `. */
  showFieldPrefix?: boolean;
  /** Optional content rendered at the top of the open panel (e.g. sort controls). */
  dropdownHeader?: ReactNode;
}

export type DateRangeFilterProps = DateRangeDropdownProps;

// =============================================================================
// PRESETS
// =============================================================================

function getQuickDateRanges(): DateRangePreset[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = shiftLocalDays(today, -1);

  const todayDow = today.getDay();
  const daysFromMonday = todayDow === 0 ? 6 : todayDow - 1;
  const thisWeekStart = shiftLocalDays(today, -daysFromMonday);
  const lastWeekStart = shiftLocalDays(thisWeekStart, -7);
  const lastWeekEnd = shiftLocalDays(thisWeekStart, -1);

  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

  // Local calendar dates: toISOString gives the UTC date, which is a day
  // early east of UTC.
  const fmt = toLocalIsoDate;

  return [
    { label: "Today", from: fmt(today), to: fmt(today) },
    { label: "Yesterday", from: fmt(yesterday), to: fmt(yesterday) },
    {
      label: "Last 7 Days",
      from: fmt(shiftLocalDays(today, -6)),
      to: fmt(today),
    },
    { label: "This Week", from: fmt(thisWeekStart), to: fmt(today) },
    { label: "Last Week", from: fmt(lastWeekStart), to: fmt(lastWeekEnd) },
    { label: "This Month", from: fmt(thisMonthStart), to: fmt(today) },
    { label: "Last Month", from: fmt(lastMonthStart), to: fmt(lastMonthEnd) },
    {
      label: "Last 30 Days",
      from: fmt(shiftLocalDays(today, -29)),
      to: fmt(today),
    },
  ];
}

// =============================================================================
// DISPLAY HELPERS
// =============================================================================

function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Local calendar date. Native date inputs are local, not UTC. */
function localTodayIso(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function matchPresetLabel(value: DateRangeValue): string | null {
  const presets = getQuickDateRanges();
  const match = presets.find((p) => p.from === value.from && p.to === value.to);
  return match?.label ?? null;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function DateRangeDropdown({
  value,
  onChange,
  placeholder = "Date range",
  disabled = false,
  className,
  "aria-label": ariaLabel,
  activePresetLabel,
  stayOpenOnClear = false,
  showFieldPrefix = false,
  dropdownHeader,
}: Readonly<DateRangeDropdownProps>) {
  const instanceId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [alignStart, setAlignStart] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const presets = useMemo(() => getQuickDateRanges(), []);

  const [draftFrom, setDraftFrom] = useState(value.from);
  const [draftTo, setDraftTo] = useState(value.to);
  const [fromError, setFromError] = useState<string | null>(null);
  const [toError, setToError] = useState<string | null>(null);
  const todayIso = localTodayIso();
  // Bound each input by the other's draft, not the applied value, so a new
  // range typed after Clear (applied value empty) is still constrained.
  const draftToBound = isReasonableDate(draftTo) ? draftTo : "";
  const draftFromBound = isReasonableDate(draftFrom) ? draftFrom : "";
  const fromMax =
    draftToBound && draftToBound <= todayIso ? draftToBound : todayIso;

  // Latest drafts and applied range, read by blur/close handlers so they never
  // act on a stale closure.
  const draftRef = useRef({ from: value.from, to: value.to });
  const appliedRef = useRef({ from: value.from, to: value.to });
  // Set by keydown on a date input so the following change event is treated as
  // typing (commit on blur/Enter) instead of a calendar pick (commit now).
  const typingRef = useRef(false);

  useEffect(() => {
    appliedRef.current = { ...appliedRef.current, from: value.from };
    draftRef.current = { ...draftRef.current, from: value.from };
    setDraftFrom(value.from);
    setFromError(null);
  }, [value.from]);

  useEffect(() => {
    appliedRef.current = { ...appliedRef.current, to: value.to };
    draftRef.current = { ...draftRef.current, to: value.to };
    setDraftTo(value.to);
    setToError(null);
  }, [value.to]);

  /**
   * Validate the drafts and apply them when the whole range is complete and
   * valid. Half-typed, rejected, or out-of-order ranges are never applied.
   * `field` is the input that triggered the commit; a range-order error is
   * shown on it.
   */
  const commitDrafts = useCallback(
    (field: "from" | "to"): boolean => {
      const { from, to } = draftRef.current;
      const fromRejection = getDateInputRejection(from, todayIso);
      const toRejection = getDateInputRejection(to, todayIso);
      setFromError(fromRejection);
      setToError(toRejection);
      if (fromRejection || toRejection) return false;
      if (!isReasonableDate(from) || !isReasonableDate(to)) return false;
      if (from && to && from > to) {
        const message = getDateRangeOrderError(from, to);
        if (field === "from") setFromError(message);
        else setToError(message);
        return false;
      }
      const applied = appliedRef.current;
      if (applied.from === from && applied.to === to) return true;
      appliedRef.current = { from, to };
      onChange({ from, to });
      return true;
    },
    [onChange, todayIso],
  );

  const closePanel = useCallback(() => {
    // A complete, valid draft that was typed but not yet blurred is applied;
    // a pending blur would otherwise run after the drafts are reset below.
    typingRef.current = false;
    commitDrafts("from");
    // Discard anything that was not applied so a stale invalid draft or error
    // is not shown the next time the panel opens.
    const applied = appliedRef.current;
    draftRef.current = { ...applied };
    setDraftFrom(applied.from);
    setDraftTo(applied.to);
    setFromError(null);
    setToError(null);
    setIsOpen(false);
  }, [commitDrafts]);

  const hasValue = value.from !== "" || value.to !== "";

  // Auto-place: flip above trigger when there isn't enough room below,
  // and align to the side with more horizontal space.
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth ?? 320;
    const panelHeight = panelRef.current?.offsetHeight ?? 400;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    setOpenAbove(spaceBelow < panelHeight && spaceAbove > spaceBelow);

    const spaceRight = window.innerWidth - triggerRect.left;
    const spaceLeft = triggerRect.right;
    setAlignStart(spaceRight >= panelWidth || spaceRight >= spaceLeft);
  }, [isOpen]);

  // Close on click outside — uses mousedown so the browser's native date
  // picker (which renders in a separate layer) doesn't trigger a close.
  useEffect(() => {
    if (!isOpen) return;

    function onMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closePanel();
      }
    }

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, closePanel]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closePanel();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closePanel]);

  const valueText = useMemo(() => {
    if (!hasValue) return null;

    const presetLabel = matchPresetLabel(value);
    if (presetLabel) return presetLabel;

    if (value.from && value.to) {
      if (value.from === value.to) return formatShortDate(value.from);
      return `${formatShortDate(value.from)} – ${formatShortDate(value.to)}`;
    }
    if (value.from) return `From ${formatShortDate(value.from)}`;
    return `Until ${formatShortDate(value.to)}`;
  }, [value, hasValue]);

  const showPrefix = showFieldPrefix && hasValue && Boolean(valueText);
  const triggerAriaLabel =
    showPrefix && valueText
      ? `${placeholder}: ${valueText}`
      : (ariaLabel ?? placeholder);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      draftRef.current = { from: "", to: "" };
      appliedRef.current = { from: "", to: "" };
      setDraftFrom("");
      setDraftTo("");
      setFromError(null);
      setToError(null);
      onChange({ from: "", to: "" });
    },
    [onChange],
  );

  const handlePreset = useCallback(
    (preset: DateRangePreset) => {
      draftRef.current = { from: preset.from, to: preset.to };
      appliedRef.current = { from: preset.from, to: preset.to };
      setDraftFrom(preset.from);
      setDraftTo(preset.to);
      setFromError(null);
      setToError(null);
      onChange({ from: preset.from, to: preset.to, presetLabel: preset.label });
      setIsOpen(false);
    },
    [onChange],
  );

  const handleDraftChange = useCallback(
    (field: "from" | "to", next: string) => {
      draftRef.current = { ...draftRef.current, [field]: next };
      if (field === "from") setDraftFrom(next);
      else setDraftTo(next);

      const rejection = getDateInputRejection(next, todayIso);
      const setError = field === "from" ? setFromError : setToError;
      const wasTyping = typingRef.current;
      typingRef.current = false;
      if (rejection) {
        setError(rejection);
        return;
      }
      setError(null);
      // Calendar picks and programmatic changes apply right away; typed
      // values wait for blur or Enter so half-typed dates are never applied.
      if (!wasTyping) commitDrafts(field);
    },
    [commitDrafts, todayIso],
  );

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleDraftChange("from", e.target.value),
    [handleDraftChange],
  );

  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleDraftChange("to", e.target.value),
    [handleDraftChange],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const field = e.currentTarget.id.endsWith("-from") ? "from" : "to";
      if (e.key === "Enter") {
        e.preventDefault();
        typingRef.current = false;
        commitDrafts(field);
        return;
      }
      if (e.key === "Tab" || e.key === "Escape") return;
      typingRef.current = true;
    },
    [commitDrafts],
  );

  // Applying inside blur re-renders the parent while focus is moving to the
  // next control, which drops focus to <body>. Apply once focus has settled.
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferCommit = useCallback(
    (field: "from" | "to") => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
      commitTimerRef.current = setTimeout(() => {
        commitTimerRef.current = null;
        commitDrafts(field);
      }, 0);
    },
    [commitDrafts],
  );

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    [],
  );

  const handleFromBlur = useCallback(() => {
    typingRef.current = false;
    deferCommit("from");
  }, [deferCommit]);

  const handleToBlur = useCallback(() => {
    typingRef.current = false;
    deferCommit("to");
  }, [deferCommit]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    if (isOpen) closePanel();
    else setIsOpen(true);
  }, [disabled, isOpen, closePanel]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger — mirrors FilterDropdown button exactly */}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={toggleOpen}
          className={cn(
            "relative flex items-center gap-1.5 rounded-lg border bg-white text-left",
            showPrefix ? "w-max" : "w-full",
            "py-1.5 pl-3 text-sm min-w-[140px]",
            hasValue && !disabled ? "pr-14" : "pr-8",
            "border-gray-300 hover:border-gray-400 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            isOpen && "border-gray-400 ring-1 ring-gray-200",
          )}
          aria-label={triggerAriaLabel}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <CalendarDays
            className={cn(
              "h-4 w-4 shrink-0",
              hasValue ? "text-gray-700" : "text-gray-400",
            )}
            aria-hidden
          />
          <span
            className={cn(
              showPrefix ? "whitespace-nowrap" : "truncate",
              hasValue ? "text-gray-900" : "text-text-tertiary",
            )}
          >
            {showPrefix ? (
              <>
                <span className="text-text-tertiary">{placeholder}: </span>
                <span>{valueText}</span>
              </>
            ) : (
              (valueText ?? placeholder)
            )}
          </span>

          <span className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronsUpDown className="h-4 w-4 text-gray-400" aria-hidden />
          </span>
        </button>

        {/* Clear button — outside main button so click doesn't toggle */}
        {hasValue && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              handleClear(e);
              if (stayOpenOnClear) {
                setIsOpen(true);
              } else {
                setIsOpen(false);
              }
            }}
            className="absolute inset-y-0 right-6 flex items-center px-1.5 z-10 group/clear"
            aria-label="Clear date filter"
          >
            <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-200 group-hover/clear:bg-gray-400 transition-colors">
              <X className="h-2.5 w-2.5 text-text-tertiary group-hover/clear:text-white transition-colors" />
            </span>
          </button>
        )}
      </div>

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className={cn(
            "absolute z-[300] w-80 overflow-hidden rounded-lg",
            alignStart ? "left-0" : "right-0",
            "bg-white shadow-lg ring-1 ring-gray-200",
            openAbove ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          {dropdownHeader ? (
            <div
              className="border-b border-gray-200 px-2 py-1.5"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
            >
              {dropdownHeader}
            </div>
          ) : null}

          {/* Quick presets */}
          <div className="border-b border-gray-200 p-2">
            <div className="grid grid-cols-2 gap-1">
              {presets.map((preset, i) => {
                const isActive = activePresetLabel
                  ? preset.label === activePresetLabel
                  : value.from === preset.from &&
                    value.to === preset.to &&
                    presets.findIndex(
                      (p) => p.from === value.from && p.to === value.to,
                    ) === i;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handlePreset(preset)}
                    className={cn(
                      "rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors text-left",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-gray-700 hover:bg-gray-50",
                    )}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom range inputs */}
          <div className="p-3 space-y-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Custom Range
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label
                  htmlFor={`${instanceId}-from`}
                  className="block text-xs font-medium text-text-tertiary mb-1"
                >
                  From
                </label>
                <input
                  id={`${instanceId}-from`}
                  type="date"
                  value={draftFrom}
                  onChange={handleFromChange}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleFromBlur}
                  max={fromMax}
                  aria-invalid={fromError ? true : undefined}
                  aria-describedby={
                    fromError ? `${instanceId}-from-error` : undefined
                  }
                  className={cn(
                    "w-full rounded-md border px-2.5 py-1.5 text-sm text-gray-900",
                    fromError ? "border-destructive" : "border-gray-200",
                    draftFrom ? "bg-white" : "bg-gray-50",
                    "focus:border-ring focus:bg-white focus:outline-none focus:ring-1 focus:ring-ring",
                  )}
                />
                {fromError ? (
                  <p
                    id={`${instanceId}-from-error`}
                    role="alert"
                    className="mt-1 text-xs leading-snug text-status-error-text"
                  >
                    {fromError}
                  </p>
                ) : null}
              </div>
              <div>
                <label
                  htmlFor={`${instanceId}-to`}
                  className="block text-xs font-medium text-text-tertiary mb-1"
                >
                  To
                </label>
                <input
                  id={`${instanceId}-to`}
                  type="date"
                  value={draftTo}
                  onChange={handleToChange}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleToBlur}
                  min={draftFromBound || undefined}
                  max={todayIso}
                  aria-invalid={toError ? true : undefined}
                  aria-describedby={
                    toError ? `${instanceId}-to-error` : undefined
                  }
                  className={cn(
                    "w-full rounded-md border px-2.5 py-1.5 text-sm text-gray-900",
                    toError ? "border-destructive" : "border-gray-200",
                    draftTo ? "bg-white" : "bg-gray-50",
                    "focus:border-ring focus:bg-white focus:outline-none focus:ring-1 focus:ring-ring",
                  )}
                />
                {toError ? (
                  <p
                    id={`${instanceId}-to-error`}
                    role="alert"
                    className="mt-1 text-xs leading-snug text-status-error-text"
                  >
                    {toError}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* Footer — clear */}
          {hasValue && (
            <div className="flex items-center justify-end border-t border-gray-200 px-3 py-2">
              <button
                type="button"
                onClick={(e) => {
                  handleClear(e);
                  if (stayOpenOnClear) {
                    setIsOpen(true);
                  } else {
                    setIsOpen(false);
                  }
                }}
                className="text-[11px] font-medium text-status-error-text hover:text-red-800"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** @deprecated Use DateRangeDropdown */
export const DateRangeFilter = DateRangeDropdown;
export default DateRangeDropdown;
